import React, { useState, useEffect, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Shield, Settings, Check, X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Button } from './ui/button';

// Cookie consent context
const CookieConsentContext = createContext(null);

// Cookie categories for GDPR compliance
const COOKIE_CATEGORIES = {
  necessary: {
    id: 'necessary',
    name: 'Cookies essentiels',
    description: 'Indispensables au fonctionnement du site. Ne peuvent pas être désactivés.',
    required: true,
    cookies: [
      { name: 'cookie_consent', purpose: 'Mémorise vos préférences de cookies', duration: '12 mois' },
      { name: 'session_id', purpose: 'Maintient votre session de navigation', duration: 'Session' }
    ]
  },
  functional: {
    id: 'functional',
    name: 'Cookies fonctionnels',
    description: 'Améliorent votre expérience (préférences, localisation).',
    required: false,
    cookies: [
      { name: 'user_location', purpose: 'Mémorise votre commune sélectionnée', duration: '30 jours' },
      { name: 'theme_preference', purpose: 'Mémorise vos préférences d\'affichage', duration: '12 mois' }
    ]
  },
  analytics: {
    id: 'analytics',
    name: 'Cookies analytiques',
    description: "M'aident à comprendre comment vous utilisez le site.",
    required: false,
    cookies: [
      { name: 'page_views', purpose: 'Compte les pages visitées (anonyme)', duration: '13 mois' }
    ]
  }
};

// Default consent state
const getDefaultConsent = () => ({
  necessary: true, // Always true, cannot be changed
  functional: false,
  analytics: false,
  timestamp: null,
  version: '1.0'
});

// Load consent from localStorage
const loadConsent = () => {
  try {
    const saved = localStorage.getItem('gdpr_cookie_consent');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate the saved consent
      if (parsed.timestamp && parsed.version) {
        return { ...getDefaultConsent(), ...parsed, necessary: true };
      }
    }
  } catch (e) {
    console.error('Error loading cookie consent:', e);
  }
  return null;
};

// Save consent to localStorage
const saveConsent = (consent) => {
  try {
    const toSave = {
      ...consent,
      necessary: true, // Always enforce necessary
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem('gdpr_cookie_consent', JSON.stringify(toSave));
    return toSave;
  } catch (e) {
    console.error('Error saving cookie consent:', e);
    return consent;
  }
};

// Clear non-essential cookies
const clearNonEssentialCookies = (consent) => {
  // Clear functional cookies if not consented
  if (!consent.functional) {
    localStorage.removeItem('user_location');
    localStorage.removeItem('theme_preference');
  }
  // Clear analytics cookies if not consented
  if (!consent.analytics) {
    localStorage.removeItem('page_views');
  }
};

// Cookie Consent Provider
export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Load consent on mount
  useEffect(() => {
    const savedConsent = loadConsent();
    if (savedConsent) {
      setConsent(savedConsent);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  // Update consent
  const updateConsent = (newConsent) => {
    const saved = saveConsent(newConsent);
    setConsent(saved);
    setShowBanner(false);
    setShowSettings(false);
    clearNonEssentialCookies(saved);
  };

  // Accept all cookies
  const acceptAll = () => {
    updateConsent({
      necessary: true,
      functional: true,
      analytics: true
    });
  };

  // Accept only necessary cookies
  const acceptNecessary = () => {
    updateConsent({
      necessary: true,
      functional: false,
      analytics: false
    });
  };

  // Open settings modal
  const openSettings = () => {
    setShowSettings(true);
  };

  // Check if a specific category is consented
  const hasConsent = (category) => {
    if (category === 'necessary') return true;
    return consent?.[category] || false;
  };

  return (
    <CookieConsentContext.Provider value={{
      consent,
      hasConsent,
      updateConsent,
      acceptAll,
      acceptNecessary,
      openSettings,
      showSettings,
      setShowSettings
    }}>
      {children}
      {showBanner && !showSettings && <CookieBanner onOpenSettings={openSettings} />}
      {showSettings && <CookieSettingsModal />}
    </CookieConsentContext.Provider>
  );
};

// Hook to use cookie consent
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

// Cookie Banner Component
const CookieBanner = ({ onOpenSettings }) => {
  const { acceptAll, acceptNecessary } = useCookieConsent();

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-2 border-emerald-500 shadow-2xl animate-slide-up"
      role="dialog"
      aria-label="Gestion des cookies"
      data-testid="cookie-banner"
    >
      <div className="container mx-auto max-w-5xl">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Icon and text */}
          <div className="flex items-start gap-4 flex-1">
            <div className="bg-emerald-100 p-3 rounded-full flex-shrink-0">
              <Cookie className="text-emerald-700" size={24} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                Respect de votre vie privée
              </h2>
              <p className="text-gray-600 text-sm">
                J'utilise des cookies pour améliorer votre expérience. Les cookies essentiels sont nécessaires au fonctionnement du site. 
                Vous pouvez accepter ou personnaliser vos choix.{' '}
                <Link to="/politique-confidentialite" className="text-emerald-700 hover:underline">
                  Politique de confidentialité
                </Link>
              </p>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 lg:flex-shrink-0">
            <Button
              variant="outline"
              onClick={onOpenSettings}
              className="text-gray-700 border-gray-300 hover:bg-gray-50"
              data-testid="cookie-settings-btn"
            >
              <Settings className="mr-2" size={16} />
              Personnaliser
            </Button>
            <Button
              variant="outline"
              onClick={acceptNecessary}
              className="text-gray-700 border-gray-300 hover:bg-gray-50"
              data-testid="cookie-reject-btn"
            >
              Refuser
            </Button>
            <Button
              onClick={acceptAll}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="cookie-accept-btn"
            >
              <Check className="mr-2" size={16} />
              Tout accepter
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Cookie Settings Modal
const CookieSettingsModal = () => {
  const { consent, updateConsent, setShowSettings, acceptAll, acceptNecessary } = useCookieConsent();
  const [tempConsent, setTempConsent] = useState({
    necessary: true,
    functional: consent?.functional || false,
    analytics: consent?.analytics || false
  });
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (categoryId) => {
    if (categoryId === 'necessary') return; // Cannot toggle necessary
    setTempConsent(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSave = () => {
    updateConsent(tempConsent);
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-label="Paramètres des cookies"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-700 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield size={28} />
              <div>
                <h2 className="text-xl font-bold">Paramètres des cookies</h2>
                <p className="text-emerald-200 text-sm">Gérez vos préférences de confidentialité</p>
              </div>
            </div>
            <button 
              onClick={() => setShowSettings(false)}
              className="text-white/80 hover:text-white p-2"
              aria-label="Fermer"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          <p className="text-gray-600 mb-6 text-sm">
            J'utilise des cookies pour différentes finalités. Vous pouvez activer ou désactiver chaque catégorie ci-dessous.
            Les cookies essentiels sont obligatoires et ne peuvent pas être désactivés.
          </p>

          {/* Cookie categories */}
          <div className="space-y-4">
            {Object.values(COOKIE_CATEGORIES).map((category) => (
              <div 
                key={category.id}
                className={`border rounded-xl overflow-hidden ${
                  category.required ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200'
                }`}
              >
                {/* Category header */}
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label={`Détails ${category.name}`}
                      >
                        {expandedCategory === category.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          {category.name}
                          {category.required && (
                            <span className="text-xs bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full">
                              Requis
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tempConsent[category.id]}
                        onChange={() => toggleCategory(category.id)}
                        disabled={category.required}
                        className="sr-only peer"
                        aria-label={`Activer ${category.name}`}
                      />
                      <div className={`w-11 h-6 rounded-full peer ${
                        category.required 
                          ? 'bg-emerald-500 cursor-not-allowed' 
                          : 'bg-gray-200 peer-checked:bg-emerald-500'
                      } peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full`}></div>
                    </label>
                  </div>
                </div>

                {/* Expanded cookie details */}
                {expandedCategory === category.id && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="pb-2">Cookie</th>
                          <th className="pb-2">Finalité</th>
                          <th className="pb-2">Durée</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-700">
                        {category.cookies.map((cookie, idx) => (
                          <tr key={idx} className="border-t border-gray-200">
                            <td className="py-2 font-mono text-xs">{cookie.name}</td>
                            <td className="py-2">{cookie.purpose}</td>
                            <td className="py-2">{cookie.duration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            onClick={acceptNecessary}
            className="text-gray-700"
          >
            Refuser tout
          </Button>
          <Button
            variant="outline"
            onClick={acceptAll}
            className="text-gray-700"
          >
            Tout accepter
          </Button>
          <Button
            onClick={handleSave}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="cookie-save-btn"
          >
            Enregistrer mes choix
          </Button>
        </div>

        {/* Links */}
        <div className="border-t border-gray-200 p-4 text-center text-sm text-gray-500">
          <Link to="/politique-confidentialite" className="text-emerald-700 hover:underline mx-2">
            Politique de confidentialité
          </Link>
          •
          <Link to="/cookies" className="text-emerald-700 hover:underline mx-2">
            Politique cookies
          </Link>
          •
          <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline mx-2">
            CNIL
          </a>
        </div>
      </div>
    </div>
  );
};

// Small cookie settings button (for footer or settings page)
export const CookieSettingsButton = ({ className = '' }) => {
  const { openSettings, setShowSettings } = useCookieConsent();

  return (
    <button
      onClick={() => setShowSettings(true)}
      className={`inline-flex items-center gap-2 text-sm text-gray-600 hover:text-emerald-700 transition-colors ${className}`}
      data-testid="cookie-settings-trigger"
    >
      <Cookie size={16} />
      Gérer les cookies
    </button>
  );
};

// GDPR consent checkbox for forms
export const GDPRConsentCheckbox = ({ 
  checked, 
  onChange, 
  required = true,
  error = false,
  className = '' 
}) => {
  return (
    <div className={`${className}`}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          required={required}
          className={`mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 ${
            error ? 'border-red-500' : ''
          }`}
          data-testid="gdpr-consent-checkbox"
        />
        <span className="text-sm text-gray-600">
          J'accepte que mes données soient traitées conformément à la{' '}
          <Link to="/politique-confidentialite" className="text-emerald-700 hover:underline" target="_blank">
            politique de confidentialité
          </Link>
          . *
        </span>
      </label>
      {error && (
        <p className="text-red-500 text-xs mt-1 ml-7">
          Vous devez accepter la politique de confidentialité pour continuer.
        </p>
      )}
    </div>
  );
};

// Marketing consent checkbox (optional, for newsletter etc.)
export const MarketingConsentCheckbox = ({ 
  checked, 
  onChange,
  className = '' 
}) => {
  return (
    <div className={`${className}`}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          data-testid="marketing-consent-checkbox"
        />
        <span className="text-sm text-gray-600">
          J'accepte de recevoir des conseils jardinage et offres par email (facultatif).
          Désabonnement possible à tout moment.
        </span>
      </label>
    </div>
  );
};

// Data request info component
export const DataRightsInfo = ({ className = '' }) => {
  return (
    <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start gap-3">
        <Info className="text-blue-500 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Vos droits RGPD</p>
          <p>
            Vous pouvez exercer vos droits d'accès, de rectification, d'effacement et de portabilité 
            de vos données à tout moment en me contactant à{' '}
            <a href="mailto:hassaningram@gmail.com" className="underline">
              hassaningram@gmail.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentProvider;
